import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { BellRing, Loader2, Plus, Trash2, Play, Pause, Radio, Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { currentPushState, subscribeToPush, unsubscribeFromPush, isPushSupported } from "../lib/push";

const INDICATOR_LABELS = {
  price: "Price",
  rsi: "RSI (14)",
  macd_hist: "MACD hist",
  ema_cross_20_50: "EMA 20 × 50 cross",
  volume: "Volume",
  sentiment: "Sentiment pulse",
  heat_24h: "24h Sentiment heat",
};

const OP_LABELS = {
  ">": "greater than",
  "<": "less than",
  ">=": "≥",
  "<=": "≤",
  cross_up: "crosses up through",
  cross_down: "crosses down through",
  trend_up: "trend flips → up",
  trend_down: "trend flips → down",
};

// Sensible operator sets per indicator
const OPS_FOR = {
  price: [">", "<", ">=", "<=", "cross_up", "cross_down"],
  rsi: ["<", ">", "cross_up", "cross_down"],
  macd_hist: [">", "<", "cross_up", "cross_down"],
  ema_cross_20_50: ["cross_up", "cross_down"],
  volume: [">", ">="],
  sentiment: [">", "<", ">=", "<=", "cross_up", "cross_down"],
  heat_24h: ["trend_up", "trend_down", ">", "<", "cross_up", "cross_down"],
};

const DEFAULTS_FOR = {
  price: 0,
  rsi: 30,
  macd_hist: 0,
  ema_cross_20_50: 0,
  volume: 20000000,
  sentiment: 0.5,
  heat_24h: 0.3,
};

export default function Alerts() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ indicators: [], operators: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, permission: "default", subscribed: false });
  const [form, setForm] = useState({
    symbol: "AAPL",
    asset_type: "stock",
    timeframe: "1h",
    logic: "AND",
    conditions: [{ indicator: "rsi", operator: "<", threshold: 30 }],
    channels: ["toast", "push"],
    note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts/rules");
      setRules(data.rules || []);
      setMeta({ indicators: data.indicators || [], operators: data.operators || [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { currentPushState().then(setPushState); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (c) => setForm((f) => ({
    ...f,
    channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c],
  }));
  const updateCondition = (idx, patch) => setForm((f) => ({
    ...f,
    conditions: f.conditions.map((c, i) => {
      if (i !== idx) return c;
      // When indicator changes, reset op + threshold to sensible defaults
      if (patch.indicator && patch.indicator !== c.indicator) {
        return {
          indicator: patch.indicator,
          operator: OPS_FOR[patch.indicator][0],
          threshold: DEFAULTS_FOR[patch.indicator],
        };
      }
      return { ...c, ...patch };
    }),
  }));
  const addCondition = () => setForm((f) => ({
    ...f,
    conditions: [...f.conditions, { indicator: "volume", operator: ">", threshold: 20000000 }],
  }));
  const removeCondition = (idx) => setForm((f) => ({
    ...f,
    conditions: f.conditions.length > 1 ? f.conditions.filter((_, i) => i !== idx) : f.conditions,
  }));

  const createRule = async () => {
    if (!form.symbol.trim()) return toast.error("Symbol required");
    setSaving(true);
    try {
      const body = { ...form, symbol: form.symbol.trim() };
      await api.post("/alerts/rules", body);
      toast.success("Alert rule created — you'll get pinged on the next cross");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const testRule = async (rule) => {
    try {
      await api.post(`/alerts/rules/${rule.id}/test`);
      toast.success("Test alert dispatched");
    } catch { toast.error("Test failed"); }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/alerts/rules/${rule.id}?active=${!rule.active}`);
      await load();
    } catch { toast.error("Toggle failed"); }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Delete rule for ${rule.symbol.toUpperCase()} ${rule.indicator}?`)) return;
    try {
      await api.delete(`/alerts/rules/${rule.id}`);
      setRules((rs) => rs.filter((r) => r.id !== rule.id));
    } catch { toast.error("Delete failed"); }
  };

  const promoteToBot = async (rule) => {
    const cash = window.prompt(`Starting cash for the bot?`, "5000");
    if (!cash) return;
    const trade = window.prompt(`$ per trade?`, "500");
    if (!trade) return;
    try {
      const { data } = await api.post("/bots/from-alert", {
        rule_id: rule.id,
        starting_cash: parseFloat(cash),
        trade_amount_usd: parseFloat(trade),
        stop_loss_pct: 3,
        take_profit_pct: 6,
      });
      toast.success(`Bot created — start it on Auto-Trade`, {
        action: { label: "Open bots", onClick: () => navigate("/bots") },
        duration: 8000,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };

  const enablePush = async () => {
    try {
      if (!(await isPushSupported())) return toast.error("This browser can't do push notifications");
      await subscribeToPush();
      setPushState(await currentPushState());
      // dispatch test
      await api.post("/push/test");
      toast.success("Browser push enabled — you should see a test notification");
    } catch (e) { toast.error(e.message || "Enable push failed"); }
  };

  const disablePush = async () => {
    try { await unsubscribeFromPush(); setPushState(await currentPushState()); toast.success("Push disabled"); }
    catch { toast.error("Disable failed"); }
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold flex items-center gap-2">
            <BellRing className="w-7 h-7" /> Alert Rules
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Get pinged the instant an indicator crosses a threshold — live toast + browser push.
          </p>
        </div>
        <Card className="p-3 rounded-2xl flex items-center gap-3" data-testid="push-panel">
          <Radio className={`w-4 h-4 ${pushState.subscribed ? "text-[hsl(var(--gain))]" : "text-muted-foreground"}`} />
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Browser push</div>
            <div className="text-sm font-medium" data-testid="push-status">
              {!pushState.supported ? "unsupported"
                : pushState.subscribed ? "enabled"
                : pushState.permission === "denied" ? "blocked (check browser settings)"
                : "off"}
            </div>
          </div>
          {pushState.supported && (pushState.subscribed
            ? <Button size="sm" variant="outline" onClick={disablePush} data-testid="push-disable-btn" className="rounded-full">Disable</Button>
            : <Button size="sm" onClick={enablePush} data-testid="push-enable-btn" className="rounded-full">Enable</Button>)}
        </Card>
      </div>

      {/* Rule builder */}
      <Card className="p-5 rounded-2xl" data-testid="rule-builder">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-display font-semibold">Create new rule</div>
          {form.conditions.length > 1 && (
            <div className="flex items-center gap-2 text-xs" data-testid="rule-logic-picker">
              <span className="text-muted-foreground">match</span>
              {["AND", "OR"].map((l) => (
                <button key={l} type="button" onClick={() => setField("logic", l)}
                        data-testid={`rule-logic-${l.toLowerCase()}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                          form.logic === l
                            ? "bg-foreground text-background border-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}>{l}</button>
              ))}
              <span className="text-muted-foreground">of the following</span>
            </div>
          )}
        </div>

        {/* Top-level fields: symbol + type + timeframe */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-3">
            <Label className="text-xs">Symbol</Label>
            <Input value={form.symbol} onChange={(e) => setField("symbol", e.target.value.toUpperCase())}
                   placeholder="AAPL" data-testid="rule-symbol" className="rounded-lg" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <select value={form.asset_type} onChange={(e) => setField("asset_type", e.target.value)}
                    data-testid="rule-asset-type"
                    className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              <option value="stock">stock</option>
              <option value="crypto">crypto</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Timeframe</Label>
            <select value={form.timeframe} onChange={(e) => setField("timeframe", e.target.value)}
                    data-testid="rule-timeframe"
                    className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              {["5m","15m","1h","4h","1d","1w"].map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>
        </div>

        {/* Condition list */}
        <div className="mt-5 space-y-3">
          {form.conditions.map((cond, i) => (
            <div key={i} data-testid={`cond-row-${i}`}
                 className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-3 rounded-xl border border-border/60 bg-secondary/20">
              <div className="md:col-span-1 text-[10px] uppercase tracking-widest text-muted-foreground pb-2">
                {i === 0 ? "IF" : form.logic}
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs">Indicator</Label>
                <select value={cond.indicator}
                        onChange={(e) => updateCondition(i, { indicator: e.target.value })}
                        data-testid={`cond-indicator-${i}`}
                        className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
                  {(meta.indicators.length ? meta.indicators : Object.keys(INDICATOR_LABELS)).map((k) => (
                    <option key={k} value={k}>{INDICATOR_LABELS[k] || k}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Operator</Label>
                <select value={cond.operator}
                        onChange={(e) => updateCondition(i, { operator: e.target.value })}
                        data-testid={`cond-operator-${i}`}
                        className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
                  {(OPS_FOR[cond.indicator] || []).map((o) => <option key={o} value={o}>{OP_LABELS[o] || o}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Threshold</Label>
                <Input type="number" step="any" value={cond.threshold}
                       onChange={(e) => updateCondition(i, { threshold: Number(e.target.value) })}
                       data-testid={`cond-threshold-${i}`} className="rounded-lg" />
              </div>
              <div className="md:col-span-1">
                <Button variant="ghost" size="sm"
                        onClick={() => removeCondition(i)}
                        disabled={form.conditions.length === 1}
                        data-testid={`cond-remove-${i}`}
                        className="text-[hsl(var(--loss))] w-full rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addCondition} data-testid="cond-add-btn"
                  className="rounded-full w-full md:w-auto">
            <Plus className="w-4 h-4 mr-1" /> Add condition
          </Button>
        </div>

        {/* Channels + submit */}
        <div className="mt-5 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Channels</Label>
            <div className="flex gap-2 pt-1">
              {["toast", "push", "email"].map((c) => (
                <button key={c} type="button" onClick={() => toggleChannel(c)}
                        data-testid={`rule-channel-${c}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          form.channels.includes(c)
                            ? "bg-foreground text-background border-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}>{c}</button>
              ))}
            </div>
          </div>
          <Button onClick={createRule} disabled={saving} data-testid="rule-create-btn" className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create rule
          </Button>
        </div>
      </Card>

      {/* Rule list */}
      <Card className="p-5 rounded-2xl" data-testid="rule-list">
        <div className="text-lg font-display font-semibold mb-4">Active rules</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="rule-list-empty">
            No rules yet — build your first one above (e.g. "TSLA RSI &lt; 25").
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="py-2 text-left font-medium">Symbol</th>
                  <th className="py-2 text-left font-medium">Rule</th>
                  <th className="py-2 text-left font-medium">TF</th>
                  <th className="py-2 text-left font-medium">Channels</th>
                  <th className="py-2 text-left font-medium">Last fired</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-border/60" data-testid={`rule-row-${r.id}`}>
                    <td className="py-2 font-medium">
                      <span className="uppercase">{r.symbol}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">{r.asset_type}</span>
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {(r.conditions && r.conditions.length > 1) ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {r.conditions.map((c, i) => (
                            <span key={i} className="flex items-center gap-1">
                              {i > 0 && <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">{r.logic || "AND"}</span>}
                              <span>{INDICATOR_LABELS[c.indicator] || c.indicator} {OP_LABELS[c.operator] || c.operator}{" "}
                                <span className="font-mono-tab text-foreground">{c.threshold}</span></span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <>{INDICATOR_LABELS[r.indicator] || r.indicator} {OP_LABELS[r.operator] || r.operator} <span className="font-mono-tab text-foreground">{r.threshold}</span></>
                      )}
                    </td>
                    <td className="py-2">{r.timeframe}</td>
                    <td className="py-2">
                      <div className="flex gap-1 flex-wrap">
                        {(r.channels || []).map((c) => (
                          <Badge key={c} variant="secondary" className="rounded-full text-[10px]">{c}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {r.last_fired_at ? new Date(r.last_fired_at).toLocaleString() : "never"}
                    </td>
                    <td className="py-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => testRule(r)} data-testid={`rule-test-${r.id}`} title="Send test alert">
                          <Radio className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => promoteToBot(r)} data-testid={`rule-promote-${r.id}`} title="Promote to auto-trade bot">
                          <Bot className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(r)} data-testid={`rule-toggle-${r.id}`} title={r.active ? "Pause" : "Resume"}>
                          {r.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeRule(r)} data-testid={`rule-delete-${r.id}`} className="text-[hsl(var(--loss))]">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
